import { ANIMATION_FRAMES_PER_SECOND, DEFAULT_ATTACK_REACH } from './attack.js';
import { MANA_FRACTION } from './mana.js';
import { spendPoints, spentOn } from './points.js';
import { TICK_RATE_HZ } from './tick.js';
/**
 * The skill trees: what a class may learn, what it costs in points, and what
 * each skill is worth at the level a character has bought.
 *
 * `points.ts` is the ledger this sits in front of. It enforces arithmetic and
 * identity and nothing else — deliberately, so a ledger over skill ids is the
 * same code as one over attributes. **Every content rule is here**, the way
 * `attack.ts`'s `admitSwing` sits in front of `combat.ts`'s `resolveAttack`:
 * the character-level gate, the prerequisites, and D2's cap of twenty hard
 * points in one skill.
 *
 * ## The shape of the tree, and why it carries thirty per class
 *
 * D2's own: **three tabs a class, ten skills a tab**, and each tab is a
 * **3-column by 6-row grid** whose rows are the level gates.
 *
 *   - "Each character class may develop 30 more unique skills. These 30
 *     additional skills are divided into 3 skill tabs (10 skills per tab)." —
 *     The Arreat Summit, "Skills: Basic Skill Information"
 *     (https://classic.battle.net/diablo2exp/skills/basics.shtml).
 *   - The grid is `SkillDesc.txt`'s `SkillPage` (1–3), `SkillRow` (1–6) and
 *     `SkillColumn` (1–3) — the Phrozen Keep's file reference
 *     (https://d2mods.info/forum/kb/viewarticle?a=370).
 *   - "These Skills are placed into 6 rows, which represent the 6 Skill tiers.
 *     While tier 1 is available at Character Level (clvl) 1, the other tiers
 *     become available at clvl 6, 12, 18, 24, and 30." — Maxroll
 *     (https://maxroll.gg/d2/resources/warlock-overview). Checked against
 *     `Skills.txt`'s `reqlevel` for all 210 tree skills: the row-to-level rule
 *     holds with no exceptions.
 *
 * So a tree is 18 cells and a class is 54, of which D2 fills 30. The nine
 * skills authored below sit at the grid positions D2 gives them, and the other
 * eighty-one are a table edit into the same shape (`TODO.md` #460). Nothing
 * about the machinery changes when they land; `SKILLS_PER_TREE` is the budget
 * a test holds the table to.
 *
 * ## The gates
 *
 * Two, and they are independent — D2 stores them in separate columns
 * (`reqlevel` and `reqskill1-3`):
 *
 *   1. **The row's character level.** `requiredLevel` is *derived* from the row
 *      rather than authored beside it, so a skill cannot be given a gate that
 *      disagrees with where it is drawn.
 *   2. **A point in every prerequisite.** "If a skill has any prerequisites…
 *      then it will not be unlocked until you place at least one skill point in
 *      each of the prerequisites" (Arreat Summit, as above). One point, not a
 *      number of them — and the requirement is transitive without being checked
 *      transitively, because a point cannot be standing on a prerequisite whose
 *      own prerequisites were not satisfied when it was bought. `respec.ts`
 *      refunds *both* ledgers at once and there is no partial refund, so that
 *      invariant cannot be broken from behind either (`TODO.md` #456).
 *
 * And the cap: **"You cannot place more than 20 points in a specific skill."**
 * (Arreat Summit, as above). Twenty is also exactly the length of every table
 * below — a skill's level *is* an index into its own rows, so the cap and the
 * data are one fact rather than two that can drift.
 *
 * ## Why the numbers are rows and not a formula
 *
 * D2 scales a skill's damage by five per-level rates in brackets — sLvl 2-8,
 * 9-16, 17-22, 23-28 and 29+ (`MinLevDam1-5`, the Phrozen Keep's `Skills.txt`
 * reference, https://d2mods.info/forum/kb/viewarticle?a=440) — which is why
 * Fire Bolt gains 1.5 damage a level to eight and 4 a level past sixteen. Rather
 * than fit a curve to that, the tables below are **the Arreat Summit's own
 * printed rows**, transcribed, exactly as `progression.ts` transcribes
 * `Experience.txt` rather than fitting it. `ramp` is used only where the source
 * itself is a straight line.
 *
 * Mana is the exception, because D2 states it as a formula and not a table:
 * `manacost = (mana + lvlmana x (sLvl - 1)) x 2^(manashift - 8)`, in 256ths
 * (same reference). It is held here in those 256ths — the unit `mana.ts`
 * already counts regeneration in — and **rounded up** to whole mana at the
 * point of spending, because `spendMana` moves whole points and a half-paid
 * cast is not a state a client could predict. That rounding is the one
 * narrowing in this file's numbers (`TODO.md` #471).
 *
 * ## What is declared rather than implemented
 *
 * **Synergies.** D2 1.10's synergy bonus is real and it is not here, for a
 * reason the shape of the slice forces: every synergy in D2 pairs a skill with
 * a **tree-mate** (Fire Bolt with Fire Ball and Meteor; Bash with Stun and
 * Concentrate), and at one skill per tree not a single pair is expressible. So
 * each row carries D2's synergy lines as data and nothing computes them — the
 * discipline `elites.ts` keeps for an affix it cannot pay. The same is true of
 * **prerequisites**: D2 has no cross-tab prerequisite anywhere in the game
 * (checked against all 21 tabs), so `deferredPrerequisites` names the ones the
 * slice cannot express — Shout's Howl — rather than pointing `prerequisites` at
 * an id that does not exist. `TODO.md` #470.
 *
 * **Soft points.** Items that grant `+1 to Fire Bolt` raise a skill's effective
 * level and do *not* unlock the tree ("Skills granted by an item do not allow
 * you to advance further down the skill tree", Arreat Summit) and do not feed
 * synergies. `skillLevel` is hard points only; nothing supplies soft ones yet.
 */
/** The three archetypes `docs/GAME-DESIGN.md` names. Three trees each. */
export const CHARACTER_CLASSES = ['melee', 'caster', 'ranged'];
/** Trees a class has. D2's three tabs. */
export const TREES_PER_CLASS = 3;
/** Skills a tree holds when it is full. D2's ten per tab, and the budget the table is held to. */
export const SKILLS_PER_TREE = 10;
/** Skills a class has when every tree is full: D2's thirty. */
export const SKILLS_PER_CLASS = SKILLS_PER_TREE * TREES_PER_CLASS;
/** Rows in one tree. D2's `SkillDesc.txt` allows 1–6. */
export const SKILL_TREE_ROWS = 6;
/** Columns in one tree. D2's `SkillDesc.txt` allows 1–3. */
export const SKILL_TREE_COLUMNS = 3;
/**
 * The character level each row unlocks at, deepest last.
 *
 * Row 1 is open to a fresh character and every row after it is six levels
 * further on. This is the whole of the depth gate — it is a property of the
 * *row*, not of the skill, which is why `requiredLevel` is derived.
 */
export const TREE_ROW_LEVELS = [1, 6, 12, 18, 24, 30];
/** D2's hard cap on one skill, and the number of rows in every table below. */
export const MAX_SKILL_POINTS = 20;
/**
 * How far a bolt, a curse or a thrown javelin reaches, in cells of clear air
 * between the two bodies.
 *
 * **A placeholder**, in exactly the sense `DEFAULT_ATTACK_REACH` is: D2 states a
 * range per skill in `Skills.txt` and none of those numbers is transcribed here.
 * One number, stated beside the rows that use it, rather than a different guess
 * at every call site (`TODO.md` #471).
 */
export const DEFAULT_SKILL_RANGE = 4;
export const SKILL_TREES = [
    { id: 'combat', classId: 'melee', label: 'Combat Skills' },
    { id: 'masteries', classId: 'melee', label: 'Combat Masteries' },
    { id: 'warcries', classId: 'melee', label: 'Warcries' },
    { id: 'fire', classId: 'caster', label: 'Fire Spells' },
    { id: 'cold', classId: 'caster', label: 'Cold Spells' },
    { id: 'curses', classId: 'caster', label: 'Curses' },
    { id: 'bow', classId: 'ranged', label: 'Bow and Crossbow Skills' },
    { id: 'javelin', classId: 'ranged', label: 'Javelin and Spear Skills' },
    { id: 'passive', classId: 'ranged', label: 'Passive and Magic Skills' },
];
const assertWhole = (what, value, low, high) => {
    // Negated so NaN is refused alongside the out-of-range values.
    if (!(Number.isInteger(value) && value >= low && value <= high)) {
        throw new RangeError(`${what} is a whole number in ${low}-${high}, received ${value}`);
    }
};
/** The character level a row of any tree unlocks at. */
export const treeRowLevel = (row) => {
    assertWhole('A skill tree row', row, 1, SKILL_TREE_ROWS);
    return TREE_ROW_LEVELS[row - 1] ?? 1;
};
/**
 * What a twenty-row table holds at this skill level.
 *
 * A level outside 1–20 is a programmer error and throws: a skill with no points
 * has no value to read at all, and there is no twenty-first row because there is
 * no twenty-first hard point.
 */
export const atSkillLevel = (table, level) => {
    if (table.length !== MAX_SKILL_POINTS) {
        throw new RangeError(`A skill table has ${MAX_SKILL_POINTS} rows, received ${table.length}`);
    }
    assertWhole('A skill level', level, 1, MAX_SKILL_POINTS);
    return table[level - 1] ?? 0;
};
/** A duration table read at one level and converted to the fixed step. */
export const skillDurationTicks = (table, level) => atSkillLevel(table, level) * TICK_RATE_HZ;
/** What one cast costs at this skill level, in whole mana. */
export const skillManaCost = (skill, level) => atSkillLevel(skill.manaCost, level);
/**
 * How many simulation ticks a skill's casting delay lasts.
 *
 * D2 counts the delay in frames at 25 fps and this simulation steps at 20 Hz,
 * so the conversion is `attackCooldownTicks`' exactly — rounded **up**, because
 * a delay that rounded down would hand back a cast the game never gave.
 */
export const skillCooldownTicks = (skill) => Math.ceil((skill.castDelayFrames * TICK_RATE_HZ) / ANIMATION_FRAMES_PER_SECOND);
/** A skill's modifier rows resolved at one level, in the item vocabulary. */
export const skillModifiers = (rows, level) => rows.map((row) => ({ stat: row.stat, damageType: row.damageType, value: atSkillLevel(row.value, level) }));
// ---------------------------------------------------------------------------
// Table builders. Every row below is written with these, so a row is data.
// ---------------------------------------------------------------------------
/** A straight line: `base` at level 1, `step` more for each point after it. */
const ramp = (base, step) => Array.from({ length: MAX_SKILL_POINTS }, (_, index) => base + step * index);
/** A number that does not move with the skill's level. */
const flat = (value) => ramp(value, 0);
/**
 * The source's own printed rows, level 1 to 20 — the builder every non-linear
 * table below is written with, and the one the eighty-one will be written with.
 *
 * Exported for that reason: authoring a skill needs `createSkill` *and* a way to
 * state its tables, and a table that is not twenty rows is a transcription error
 * rather than a game state, so it throws where it is written instead of where it
 * is read.
 */
export const skillTable = (...values) => {
    if (values.length !== MAX_SKILL_POINTS) {
        throw new RangeError(`A skill table has ${MAX_SKILL_POINTS} rows, received ${values.length}`);
    }
    return values;
};
const modifier = (stat, value, damageType = null) => ({
    stat,
    damageType,
    value,
});
/** Flat added damage, min and max together — the shape D2 prints a single "Damage +N" row in. */
const addedDamage = (value, damageType) => [
    modifier('damageMin', value, damageType),
    modifier('damageMax', value, damageType),
];
const ZERO = flat(0);
/**
 * Mints a skill row.
 *
 * The grid position is validated here rather than trusted to the table, so "a
 * skill sits in a cell of a 3x6 tree" is an invariant of the type; and
 * `requiredLevel` is computed from the row so it cannot disagree with where the
 * skill is drawn.
 */
export const createSkill = (input) => {
    assertWhole('A skill column', input.column, 1, SKILL_TREE_COLUMNS);
    const base = input.manaBase256 ?? 0;
    const perLevel = input.manaPerLevel256 ?? 0;
    return {
        id: input.id,
        name: input.name,
        classId: input.classId,
        treeId: input.treeId,
        row: input.row,
        column: input.column,
        requiredLevel: treeRowLevel(input.row),
        prerequisites: input.prerequisites ?? [],
        deferredPrerequisites: input.deferredPrerequisites ?? [],
        synergies: input.synergies ?? [],
        // D2's cost is linear in 256ths; core spends whole mana, so it rounds up.
        manaCost: Array.from({ length: MAX_SKILL_POINTS }, (_, index) => Math.ceil((base + perLevel * index) / MANA_FRACTION)),
        castDelayFrames: input.castDelayFrames ?? 0,
        rangeCells: input.rangeCells ?? DEFAULT_SKILL_RANGE,
        target: input.target,
        effect: input.effect,
        unimplemented: input.unimplemented,
    };
};
// ---------------------------------------------------------------------------
// The nine. Every number below is the Arreat Summit's own, from the page named
// against the row; every grid position is D2's `SkillDesc.txt`.
// ---------------------------------------------------------------------------
export const SKILLS = [
    // --- melee ---------------------------------------------------------------
    createSkill({
        id: 'bash',
        name: 'Bash',
        classId: 'melee',
        treeId: 'combat',
        // Barbarian, Combat Skills, row 1 column 2, Required Level 1, no prerequisites.
        // https://classic.battle.net/diablo2exp/skills/barbarian-combatskills.shtml
        row: 1,
        column: 2,
        // "Mana Cost: 2" at every level.
        manaBase256: 2 * MANA_FRACTION,
        rangeCells: DEFAULT_ATTACK_REACH,
        target: 'enemy',
        synergies: [
            { skill: 'Stun', bonus: '+5% Damage Per Level' },
            { skill: 'Concentrate', bonus: '+5% Attack Rating Per Level' },
        ],
        effect: {
            kind: 'weapon-attack',
            // "Damage +1…+20", "Damage +% 50…145", "Attack +% 20…115".
            modifiers: [...addedDamage(ramp(1, 1), 'physical'), modifier('enhancedDamagePercent', ramp(50, 5))],
            attackRatingPercent: ramp(20, 5),
            convertedPercent: ZERO,
            convertedTo: null,
        },
        unimplemented: 'D2 knocks the target back ("A powerful smashing blow that knocks the target back"). Core has no knockback: world.ts moves a body along a path and nothing can displace one, so the damage lands and the stagger does not.',
    }),
    createSkill({
        id: 'iron-skin',
        name: 'Iron Skin',
        classId: 'melee',
        treeId: 'masteries',
        // Barbarian, Combat Masteries, row 4 column 3, Required Level 18, no prerequisites.
        // https://classic.battle.net/diablo2exp/skills/barbarian-combatmasteries.shtml
        row: 4,
        column: 3,
        target: 'none',
        effect: {
            kind: 'passive',
            // "Increase Defense Rating % 30…220".
            modifiers: [modifier('enhancedDefensePercent', ramp(30, 10))],
            criticalChancePercent: ZERO,
        },
        unimplemented: null,
    }),
    createSkill({
        id: 'shout',
        name: 'Shout',
        classId: 'melee',
        treeId: 'warcries',
        // Barbarian, Warcries, row 2 column 2, Required Level 6, Prerequisites: Howl [1].
        // https://classic.battle.net/diablo2exp/skills/barbarian-warcries.shtml
        row: 2,
        column: 2,
        deferredPrerequisites: ['Howl'],
        // "Mana Cost: 6" at every level.
        manaBase256: 6 * MANA_FRACTION,
        target: 'self',
        synergies: [
            { skill: 'Battle Orders', bonus: '+5 Seconds Per Level' },
            { skill: 'Battle Command', bonus: '+5 Seconds Per Level' },
        ],
        effect: {
            kind: 'timed-modifier',
            effectId: 'shout',
            // "Defense Bonus % 100…290" for "Duration (seconds) 20…210".
            modifiers: [modifier('enhancedDefensePercent', ramp(100, 10))],
            durationSeconds: ramp(20, 10),
        },
        unimplemented: 'D2 "increases the defense of friendly units" — every party member in a radius. Core has no party and no area targeting, so it buffs the caster alone; the other players are `TODO.md` #472.',
    }),
    // --- caster --------------------------------------------------------------
    createSkill({
        id: 'fire-bolt',
        name: 'Fire Bolt',
        classId: 'caster',
        treeId: 'fire',
        // Sorceress, Fire Spells, row 1 column 2, Required Level 1, no prerequisites.
        // https://classic.battle.net/diablo2exp/skills/sorceress-fire.shtml
        row: 1,
        column: 2,
        // "Mana Cost: 2.5" at every level — D2's `mana 5, manashift 7`.
        manaBase256: 640,
        target: 'enemy',
        synergies: [
            { skill: 'Fire Ball', bonus: '+16% Fire Damage Per Level' },
            { skill: 'Meteor', bonus: '+16% Fire Damage Per Level' },
        ],
        effect: {
            kind: 'spell-damage',
            damageType: 'fire',
            // "Damage 3-6 … 45-60".
            minimum: skillTable(3, 4, 6, 7, 9, 10, 12, 13, 15, 17, 19, 21, 23, 25, 27, 29, 33, 37, 41, 45),
            maximum: skillTable(6, 7, 9, 10, 12, 13, 15, 16, 19, 22, 25, 28, 31, 34, 37, 40, 45, 50, 55, 60),
        },
        unimplemented: 'D2 fires a travelling missile that can be dodged or blocked by a body in the way. Core has no missile entity, so the bolt is resolved against the named target the instant it is cast.',
    }),
    createSkill({
        id: 'ice-bolt',
        name: 'Ice Bolt',
        classId: 'caster',
        treeId: 'cold',
        // Sorceress, Cold Spells, row 1 column 2, Required Level 1, no prerequisites.
        // https://classic.battle.net/diablo2exp/skills/sorceress-cold.shtml
        row: 1,
        column: 2,
        // "Mana Cost: 3" at every level.
        manaBase256: 3 * MANA_FRACTION,
        target: 'enemy',
        synergies: [
            { skill: 'Frost Nova', bonus: '+15% Cold Damage Per Level' },
            { skill: 'Ice Blast', bonus: '+15% Cold Damage Per Level' },
            { skill: 'Glacial Spike', bonus: '+15% Cold Damage Per Level' },
            { skill: 'Blizzard', bonus: '+15% Cold Damage Per Level' },
            { skill: 'Frozen Orb', bonus: '+15% Cold Damage Per Level' },
        ],
        effect: {
            kind: 'spell-damage',
            damageType: 'cold',
            // "Damage 3-5 … 38-49".
            minimum: skillTable(3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 22, 24, 26, 29, 32, 35, 38),
            maximum: skillTable(5, 6, 8, 9, 11, 12, 14, 15, 18, 20, 23, 25, 28, 30, 33, 36, 39, 42, 46, 49),
        },
        unimplemented: 'The "Cold Length (seconds) 6…32.6" is dropped: core has no chill and no freeze, exactly as elites.ts says of Cold Enchanted, so the damage lands and the slow does not. It is also a missile, as Fire Bolt is.',
    }),
    createSkill({
        id: 'amplify-damage',
        name: 'Amplify Damage',
        classId: 'caster',
        treeId: 'curses',
        // Necromancer, Curses, row 1 column 2, Required Level 1, no prerequisites.
        // https://classic.battle.net/diablo2exp/skills/necromancer-curses.shtml
        row: 1,
        column: 2,
        // "Mana Cost: 4" at every level.
        manaBase256: 4 * MANA_FRACTION,
        target: 'enemy',
        effect: {
            kind: 'timed-modifier',
            effectId: 'amplify-damage',
            // "Amplify Damage lowers monster Physical Resistances by 100%" — a flat
            // 100 at every level; only "Duration (seconds) 8…65" grows.
            modifiers: [modifier('resistancePercent', flat(-100), 'physical')],
            durationSeconds: ramp(8, 3),
        },
        unimplemented: 'D2 curses every monster inside a radius ("Radius (yards) 2…14.6") and allows only one curse on a monster at a time. Core has no area targeting, so it lands on the one named body, and effects.ts holds one effect per id rather than one per curse class.',
    }),
    // --- ranged --------------------------------------------------------------
    createSkill({
        id: 'cold-arrow',
        name: 'Cold Arrow',
        classId: 'ranged',
        treeId: 'bow',
        // Amazon, Bow and Crossbow, row 2 column 1, Required Level 6, no prerequisites.
        // https://classic.battle.net/diablo2exp/skills/amazon-bow.shtml
        row: 2,
        column: 1,
        // "Mana Cost 3.5 … 5.8" — linear at +0.125 a level.
        manaBase256: 896,
        manaPerLevel256: 32,
        target: 'enemy',
        synergies: [{ skill: 'Ice Arrow', bonus: '+12% Cold Damage Per Level' }],
        effect: {
            kind: 'weapon-attack',
            // "Cold Damage 3-4 … 53-56".
            modifiers: [
                modifier('damageMin', skillTable(3, 5, 7, 9, 11, 13, 15, 17, 19, 22, 24, 27, 29, 32, 34, 37, 41, 45, 49, 53), 'cold'),
                modifier('damageMax', skillTable(4, 6, 8, 10, 12, 14, 16, 18, 20, 23, 25, 28, 30, 33, 35, 38, 42, 47, 51, 56), 'cold'),
            ],
            // "+% To Attack Rating 10…181".
            attackRatingPercent: ramp(10, 9),
            // "Converts X% Physical Damage To Elemental Damage 3…41".
            convertedPercent: ramp(3, 2),
            convertedTo: 'cold',
        },
        unimplemented: 'The "Cold Length (seconds) 4…26.8" is dropped for the reason Ice Bolt\'s is — core has no chill. The arrow is also a missile, and nothing checks that a bow is equipped: items.ts has categories but no skill requires one yet.',
    }),
    createSkill({
        id: 'poison-javelin',
        name: 'Poison Javelin',
        classId: 'ranged',
        treeId: 'javelin',
        // Amazon, Javelin and Spear, row 2 column 3, Required Level 6, no
        // prerequisites, "Casting Delay: 0.6 Seconds" — 15 frames in Skills.txt.
        // https://classic.battle.net/diablo2exp/skills/amazon-javelin.shtml
        row: 2,
        column: 3,
        // "Mana Cost 4 … 8.7" — linear at +0.25 a level.
        manaBase256: 1_024,
        manaPerLevel256: 64,
        castDelayFrames: 15,
        target: 'enemy',
        synergies: [{ skill: 'Plague Javelin', bonus: '+12% Poison Damage Per Level' }],
        effect: {
            kind: 'damage-over-time',
            damageType: 'poison',
            // "Poison Damage 25-37 … 2659-2946" over "Duration (Over X seconds) 8…46".
            minimum: skillTable(25, 46, 75, 109, 150, 196, 250, 309, 412, 528, 656, 796, 950, 1_115, 1_293, 1_484, 1_750, 2_034, 2_337, 2_659),
            maximum: skillTable(37, 62, 93, 131, 175, 225, 281, 343, 459, 589, 732, 890, 1_062, 1_248, 1_448, 1_662, 1_953, 2_264, 2_595, 2_946),
            durationSeconds: ramp(8, 2),
        },
        unimplemented: 'D2 leaves "a trail of poison clouds" along the javelin\'s flight, which is a second source of poison on the ground. Core has no missile and no ground effect, so the whole promise lands on the body it was thrown at.',
    }),
    createSkill({
        id: 'critical-strike',
        name: 'Critical Strike',
        classId: 'ranged',
        treeId: 'passive',
        // Amazon, Passive and Magic, row 1 column 3, Required Level 1, no prerequisites.
        // https://classic.battle.net/diablo2exp/skills/amazon-passive.shtml
        row: 1,
        column: 3,
        target: 'none',
        effect: {
            kind: 'passive',
            modifiers: [],
            // "Chance to 2x Damage % 16…68" — sharply diminishing, so the rows are
            // the source's own rather than a line fitted to them.
            criticalChancePercent: skillTable(16, 25, 32, 38, 42, 46, 49, 51, 54, 56, 58, 59, 61, 62, 63, 65, 65, 66, 67, 68),
        },
        unimplemented: null,
    }),
];
// ---------------------------------------------------------------------------
// Lookups.
// ---------------------------------------------------------------------------
/**
 * The skill with this id, or `undefined`.
 *
 * Absence is not an error here and the caller decides, as it does for
 * `itemBaseById`: this is the one lookup a client's message reaches, so it has
 * to be able to be wrong.
 */
export const skillById = (id, catalogue = SKILLS) => catalogue.find((skill) => skill.id === id);
/** Every tree a class has, in table order. */
export const treesOfClass = (classId, trees = SKILL_TREES) => trees.filter((tree) => tree.classId === classId);
/** Every authored skill in one tree, in table order. */
export const treeSkills = (treeId, catalogue = SKILLS) => catalogue.filter((skill) => skill.treeId === treeId);
/** Every authored skill of one class, in table order. */
export const classSkills = (classId, catalogue = SKILLS) => catalogue.filter((skill) => skill.classId === classId);
/**
 * The skill ids a character of this class may spend on.
 *
 * What `createCharacterProgress` opens its skill ledger over — the missing half
 * of `TODO.md` #456. The ledger is the authority on what a character owns:
 * anything not in this list is refused as an unknown key, which is exactly the
 * answer a client naming another class's skill deserves.
 */
export const classSkillIds = (classId, catalogue = SKILLS) => classSkills(classId, catalogue).map((skill) => skill.id);
// ---------------------------------------------------------------------------
// Gates, and spending a point through them.
// ---------------------------------------------------------------------------
/** Hard points standing on a skill. Zero for one this character holds no key for. */
export const skillLevel = (progress, skillId) => spentOn(progress.skillPoints, skillId);
/**
 * The gate standing in front of a skill, or `null` when there is none.
 *
 * The level is answered first because it is the cheaper question and because it
 * is the one a player can do nothing about but level up; a prerequisite is a
 * choice they can make right now.
 */
export const skillLockReason = (skill, progress) => {
    if (progress.level < skill.requiredLevel)
        return 'level';
    if (skill.prerequisites.some((id) => skillLevel(progress, id) === 0))
        return 'prerequisite';
    return null;
};
/** Whether both gates are open. */
export const isSkillUnlocked = (skill, progress) => skillLockReason(skill, progress) === null;
/**
 * Puts points into a skill, or refuses the whole request.
 *
 * This is the layer `points.ts` deliberately does not have. It answers, in
 * order: is there such a skill, does this character hold a key for it, is the
 * amount whole, are both gates open, and does the request stay inside D2's cap
 * — and only then does it hand the arithmetic to the ledger.
 *
 * A skill that exists but belongs to another class comes back `'unknown-key'`,
 * which is the ledger's own refusal and is the correct one: the character was
 * never granted a key for it.
 */
export const spendSkillPoint = (progress, skillId, points = 1, catalogue = SKILLS) => {
    const skill = skillById(skillId, catalogue);
    if (skill === undefined)
        return { ok: false, reason: 'unknown-skill' };
    if (!Object.hasOwn(progress.skillPoints.spent, skillId))
        return { ok: false, reason: 'unknown-key' };
    // Negated so NaN is refused with the out-of-range values.
    if (!(Number.isInteger(points) && points >= 0))
        return { ok: false, reason: 'invalid-amount' };
    const lock = skillLockReason(skill, progress);
    if (lock === 'level')
        return { ok: false, reason: 'level-locked' };
    if (lock === 'prerequisite')
        return { ok: false, reason: 'prerequisite' };
    if (skillLevel(progress, skillId) + points > MAX_SKILL_POINTS)
        return { ok: false, reason: 'maxed' };
    const spend = spendPoints(progress.skillPoints, skillId, points);
    if (!spend.ok)
        return { ok: false, reason: spend.reason };
    return { ok: true, progress: { ...progress, skillPoints: spend.ledger }, spent: spend.spent };
};
export const passiveContribution = (progress, catalogue = SKILLS) => {
    // Narrowed once, into the pair every line below needs: a second
    // `kind === 'passive'` inside each reducer would be a branch nothing can take.
    const invested = catalogue.flatMap((skill) => {
        const level = skillLevel(progress, skill.id);
        return skill.effect.kind === 'passive' && level > 0 ? [{ effect: skill.effect, level }] : [];
    });
    return {
        modifiers: invested.flatMap((entry) => skillModifiers(entry.effect.modifiers, entry.level)),
        criticalChancePercent: invested.reduce((total, entry) => total + atSkillLevel(entry.effect.criticalChancePercent, entry.level), 0),
    };
};
//# sourceMappingURL=skills.js.map